import { useCallback, useRef, useMemo, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextAa,
  Hash,
  MathOperations,
  Image,
  ListBullets,
  TreeStructure,
  CaretRight,
  X,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useUIStore, type BottomPanelTab } from "../../stores/ui";
import { useDocumentStore } from "../../stores/document";

type BottomTab = BottomPanelTab;

interface BottomPanelProps {
  errorsContent?: ReactNode;
  logsContent?: ReactNode;
  versionsContent?: ReactNode;
  vitalsContent?: ReactNode;
  errorCount?: number;
  className?: string;
}

const TABS: { id: BottomTab; label: string }[] = [
  { id: "errors", label: "Errors" },
  { id: "logs", label: "Logs" },
  { id: "outline", label: "Outline" },
  { id: "summary", label: "Summary" },
  { id: "vitals", label: "Vitals" },
  { id: "versions", label: "Versions" },
];

const MIN_HEIGHT = 100;
const MAX_HEIGHT = 500;

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };

function ErrorBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span
      className={cn(
        "ml-1 inline-flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-1",
        "text-[9px] font-semibold leading-none",
        "bg-[var(--color-soft-red)]/15 text-[var(--color-soft-red)]",
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

interface DocumentStats {
  wordsInText: number;
  wordsInHeaders: number;
  wordsOutsideText: number;
  totalWords: number;
  headers: number;
  figures: number;
  mathInline: number;
  mathDisplay: number;
  tables: number;
  citations: number;
  labels: number;
  paragraphs: number;
  characters: number;
}

function analyzeLatex(content: string): DocumentStats {
  const withoutComments = content.replace(/%.*$/gm, "");

  const headerPatterns = /\\(?:section|subsection|subsubsection|chapter|part)\*?\{([^}]*)\}/g;
  let headers = 0;
  let wordsInHeaders = 0;
  let match: RegExpExecArray | null;
  while ((match = headerPatterns.exec(withoutComments)) !== null) {
    headers++;
    wordsInHeaders += match[1].trim().split(/\s+/).filter(Boolean).length;
  }

  const figures = (withoutComments.match(/\\begin\{figure\}/g) || []).length;
  const tables = (withoutComments.match(/\\begin\{table\}/g) || []).length;
  const mathInline = (withoutComments.match(/(?<![\\$])\$(?!\$).*?\$(?!\$)/g) || []).length;
  const mathDisplay = (withoutComments.match(/\$\$[\s\S]*?\$\$/g) || []).length
    + (withoutComments.match(/\\begin\{(?:equation|align|gather|multline)\*?\}/g) || []).length;
  const citations = (withoutComments.match(/\\cite\{[^}]*\}/g) || []).length;
  const labels = (withoutComments.match(/\\label\{[^}]*\}/g) || []).length;

  const stripped = withoutComments
    .replace(/\\begin\{.*?\}[\s\S]*?\\end\{.*?\}/g, "")
    .replace(/\\[a-zA-Z]+(\{[^}]*\})*/g, " ")
    .replace(/[{}[\]$\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const allWords = stripped.split(/\s+/).filter((w) => w.length > 0 && /[a-zA-Z]/.test(w));
  const totalWords = allWords.length;
  const wordsOutsideText = 0;
  const wordsInText = totalWords - wordsInHeaders;
  const characters = content.length;
  const paragraphs = content.split(/\n\s*\n/).filter((p) => p.trim().length > 0).length;

  return {
    wordsInText,
    wordsInHeaders,
    wordsOutsideText,
    totalWords,
    headers,
    figures,
    mathInline,
    mathDisplay,
    tables,
    citations,
    labels,
    paragraphs,
    characters,
  };
}

function StatItem({ icon: Icon, label, value }: { icon: typeof TextAa; label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between py-1.5 px-3 hover:bg-[var(--bg-surface-hover)] transition-colors duration-75 rounded-[var(--radius-sm)]">
      <div className="flex items-center gap-2">
        <Icon size={12} className="text-[var(--text-muted)] shrink-0" />
        <span className="text-[11px] text-[var(--text-secondary)]">{label}</span>
      </div>
      <span className="text-[11px] font-semibold tabular-nums text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

interface OutlineHeading {
  level: number;
  title: string;
  line: number;
  kind: string;
}

const LATEX_HEADING_RE =
  /\\(part|chapter|section|subsection|subsubsection|paragraph|subparagraph)\*?\{([^}]*)\}/g;

const LATEX_LEVEL: Record<string, number> = {
  part: 0,
  chapter: 1,
  section: 2,
  subsection: 3,
  subsubsection: 4,
  paragraph: 5,
  subparagraph: 6,
};

function parseOutlineLatex(content: string): OutlineHeading[] {
  const headings: OutlineHeading[] = [];
  const lines = content.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trimStart().startsWith("%")) continue;
    let m: RegExpExecArray | null;
    LATEX_HEADING_RE.lastIndex = 0;
    while ((m = LATEX_HEADING_RE.exec(line)) !== null) {
      headings.push({
        level: LATEX_LEVEL[m[1]] ?? 2,
        title: m[2].replace(/\\[a-zA-Z]+\{([^}]*)\}/g, "$1").trim(),
        line: i + 1,
        kind: m[1],
      });
    }
  }
  return headings;
}

function parseOutlineMarkdown(content: string): OutlineHeading[] {
  const headings: OutlineHeading[] = [];
  const lines = content.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(/^(#{1,6})\s+(.+)/);
    if (match) {
      headings.push({
        level: match[1].length,
        title: match[2].replace(/[*_`#]/g, "").trim(),
        line: i + 1,
        kind: "#".repeat(match[1].length),
      });
    }
  }
  return headings;
}

function parseOutlineDocx(content: string): OutlineHeading[] {
  const headings: OutlineHeading[] = [];
  const lines = content.split("\n");
  const regex = /<h([1-6])[^>]*>(.*?)<\/h\1>/i;
  for (let i = 0; i < lines.length; i++) {
    const match = regex.exec(lines[i]);
    if (match) {
      headings.push({
        level: parseInt(match[1]),
        title: match[2].replace(/<[^>]*>/g, "").trim(),
        line: i + 1,
        kind: `H${match[1]}`,
      });
    }
  }
  return headings;
}

function analyzeDocx(content: string): DocumentStats {
  const stripped = content.replace(/<[^>]*>/g, " ");
  const words = stripped.split(/\s+/).filter((w) => w.length > 0 && /[a-zA-Z]/.test(w));
  const totalWords = words.length;
  const headers = (content.match(/<h[1-6][^>]*>/gi) || []).length;
  const figures = (content.match(/<img\s/gi) || []).length;
  const tables = (content.match(/<table/gi) || []).length;
  const links = (content.match(/<a\s/gi) || []).length;
  const lists = (content.match(/<[ou]l/gi) || []).length;
  const paragraphs = (content.match(/<p[^>]*>/gi) || []).filter(Boolean).length;

  return {
    wordsInText: totalWords,
    wordsInHeaders: 0,
    wordsOutsideText: 0,
    totalWords,
    headers,
    figures,
    mathInline: 0,
    mathDisplay: 0,
    tables,
    citations: links,
    labels: lists,
    paragraphs,
    characters: stripped.replace(/\s+/g, "").length,
  };
}

const LEVEL_COLORS: Record<number, string> = {
  0: "var(--color-lever-orange)",
  1: "var(--color-lever-orange)",
  2: "var(--text-primary)",
  3: "var(--text-secondary)",
  4: "var(--text-muted)",
  5: "var(--text-muted)",
  6: "var(--text-muted)",
};

const LEVEL_WEIGHT: Record<number, string> = {
  0: "font-bold",
  1: "font-bold",
  2: "font-semibold",
  3: "font-medium",
  4: "font-normal",
  5: "font-normal",
  6: "font-normal",
};

const LEVEL_SIZE: Record<number, string> = {
  0: "text-[12px]",
  1: "text-[12px]",
  2: "text-[11px]",
  3: "text-[11px]",
  4: "text-[10px]",
  5: "text-[10px]",
  6: "text-[10px]",
};

function OutlineContent() {
  const content = useDocumentStore((s) => s.currentDocument?.content || "");
  const docType = useDocumentStore((s) => s.currentDocument?.doc_type || "latex");

  const headings = useMemo(() => {
    if (docType === "latex") return parseOutlineLatex(content);
    if (docType === "docx") return parseOutlineDocx(content);
    return parseOutlineMarkdown(content);
  }, [content, docType]);

  const minLevel = useMemo(
    () => (headings.length > 0 ? Math.min(...headings.map((h) => h.level)) : 0),
    [headings],
  );

  if (headings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 py-6 text-center">
        <TreeStructure size={20} className="text-[var(--text-muted)] mb-1.5" />
        <p className="text-[11px] text-[var(--text-muted)]">
          No headings found in your document
        </p>
        <p className="text-[10px] text-[var(--text-muted)] mt-0.5 opacity-60">
          {docType === "latex"
            ? "Use \\section{}, \\subsection{}, etc."
            : docType === "docx"
              ? "Use Heading 1, Heading 2, etc. from the toolbar"
              : "Use # Heading, ## Subheading, etc."}
        </p>
      </div>
    );
  }

  return (
    <div className="py-1.5 px-1">
      {headings.map((h, idx) => {
        const indent = h.level - minLevel;
        return (
          <div
            key={`${h.line}-${idx}`}
            className={cn(
              "group flex items-center gap-1.5 py-[3px] px-2 rounded-[var(--radius-sm)] min-w-0",
              "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75 cursor-default",
            )}
            style={{ paddingLeft: `${8 + indent * 16}px` }}
          >
            {/* Indent guide dots */}
            {indent > 0 && (
              <CaretRight
                size={8}
                weight="bold"
                className="shrink-0 opacity-25"
                style={{ color: LEVEL_COLORS[h.level] ?? "var(--text-muted)" }}
              />
            )}

            {/* Level badge */}
            <span
              className={cn(
                "shrink-0 min-w-[18px] text-center rounded-[3px] px-1 py-px",
                "text-[8px] font-mono uppercase tracking-wider leading-tight",
                "bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]",
                h.level <= 1
                  ? "text-[var(--color-lever-orange)]"
                  : "text-[var(--text-muted)]",
              )}
            >
              {docType === "latex"
                ? h.kind.replace("subsub", "ss").replace("sub", "s").slice(0, 4)
                : h.kind}
            </span>

            {/* Title */}
            <span
              className={cn(
                "flex-1 min-w-0 truncate leading-snug",
                LEVEL_SIZE[h.level] ?? "text-[10px]",
                LEVEL_WEIGHT[h.level] ?? "font-normal",
              )}
              style={{ color: LEVEL_COLORS[h.level] ?? "var(--text-muted)" }}
              title={h.title}
            >
              {h.title || <span className="italic opacity-40">Untitled</span>}
            </span>

            {/* Line number */}
            <span className="ml-auto shrink-0 text-[9px] tabular-nums text-[var(--text-muted)] opacity-0 group-hover:opacity-60 transition-opacity duration-75">
              L{h.line}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SummaryContent() {
  const content = useDocumentStore((s) => s.currentDocument?.content || "");
  const docType = useDocumentStore((s) => s.currentDocument?.doc_type || "latex");

  const stats = useMemo(() => {
    if (docType === "latex") return analyzeLatex(content);
    if (docType === "docx") return analyzeDocx(content);
    const words = content.split(/\s+/).filter(Boolean).length;
    const headers = (content.match(/^#{1,6}\s/gm) || []).length;
    const paragraphs = content.split(/\n\s*\n/).filter((p) => p.trim().length > 0).length;
    return {
      wordsInText: words,
      wordsInHeaders: 0,
      wordsOutsideText: 0,
      totalWords: words,
      headers,
      figures: (content.match(/!\[/g) || []).length,
      mathInline: (content.match(/(?<![\\$])\$(?!\$).*?\$(?!\$)/g) || []).length,
      mathDisplay: (content.match(/\$\$[\s\S]*?\$\$/g) || []).length,
      tables: (content.match(/\|.*\|/gm) || []).filter((_, i) => i % 2 === 0).length > 0 ? 1 : 0,
      citations: 0,
      labels: 0,
      paragraphs,
      characters: content.length,
    } satisfies DocumentStats;
  }, [content, docType]);

  return (
    <div className="flex gap-6 p-2 overflow-x-auto">
      {/* Word counts */}
      <div className="min-w-[180px]">
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Words
        </div>
        <div className="flex flex-col">
          <StatItem icon={TextAa} label="Total" value={stats.totalWords} />
          <StatItem icon={TextAa} label="In text body" value={stats.wordsInText} />
          <StatItem icon={TextAa} label="In headers" value={stats.wordsInHeaders} />
          <StatItem icon={TextAa} label="Characters" value={stats.characters.toLocaleString()} />
          <StatItem icon={ListBullets} label="Paragraphs" value={stats.paragraphs} />
        </div>
      </div>

      {/* Structure */}
      <div className="min-w-[180px]">
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Structure
        </div>
        <div className="flex flex-col">
          <StatItem icon={Hash} label="Headings" value={stats.headers} />
          <StatItem icon={Image} label="Figures" value={stats.figures} />
          <StatItem icon={ListBullets} label="Tables" value={stats.tables} />
          <StatItem icon={MathOperations} label="Math (inline)" value={stats.mathInline} />
          <StatItem icon={MathOperations} label="Math (display)" value={stats.mathDisplay} />
        </div>
      </div>

      {docType === "latex" && (
        <div className="min-w-[180px]">
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            References
          </div>
          <div className="flex flex-col">
            <StatItem icon={Hash} label="Citations" value={stats.citations} />
            <StatItem icon={Hash} label="Labels" value={stats.labels} />
          </div>
        </div>
      )}
      {docType === "docx" && (
        <div className="min-w-[180px]">
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Elements
          </div>
          <div className="flex flex-col">
            <StatItem icon={Hash} label="Links" value={stats.citations} />
            <StatItem icon={ListBullets} label="Lists" value={stats.labels} />
          </div>
        </div>
      )}
    </div>
  );
}

export function BottomPanel({
  errorsContent,
  logsContent,
  versionsContent,
  vitalsContent,
  errorCount = 0,
  className,
}: BottomPanelProps) {
  const open = useUIStore((s) => s.bottomPanelOpen);
  const setOpen = useUIStore((s) => s.setBottomPanelOpen);
  const height = useUIStore((s) => s.bottomPanelHeight);
  const setHeight = useUIStore((s) => s.setBottomPanelHeight);
  const activeTab = useUIStore((s) => s.bottomPanelTab);
  const setActiveTab = useUIStore((s) => s.setBottomPanelTab);
  const dragging = useRef(false);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragging.current = true;
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";

      const startY = e.clientY;
      const startHeight = height;

      const onPointerMove = (ev: PointerEvent) => {
        if (!dragging.current) return;
        const delta = startY - ev.clientY;
        setHeight(Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startHeight + delta)));
      };

      const onPointerUp = () => {
        dragging.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [height, setHeight],
  );

  const tabContent: Record<BottomTab, ReactNode> = {
    errors: errorsContent,
    logs: logsContent,
    outline: <OutlineContent />,
    summary: <SummaryContent />,
    vitals: vitalsContent ?? (
      <div className="flex items-center justify-center p-6 text-xs text-[var(--text-muted)]">
        Start writing to see document vitals.
      </div>
    ),
    versions: versionsContent ?? (
      <div className="flex items-center justify-center p-6 text-xs text-[var(--text-muted)]">
        Version history coming soon
      </div>
    ),
  };

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height }}
          exit={{ height: 0 }}
          transition={spring}
          className={cn(
            "relative flex shrink-0 flex-col overflow-hidden",
            "bg-[var(--bg-surface)] border-t border-[var(--border-hairline)]",
            className,
          )}
          style={{ willChange: "height" }}
        >
          {/* Resize handle */}
          <div
            onPointerDown={onResizePointerDown}
            className="group absolute inset-x-0 top-0 z-20 h-[5px] cursor-row-resize touch-none select-none"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-transparent transition-all duration-150 group-hover:h-[3px] group-hover:bg-[var(--text-muted)] group-hover:opacity-40" />
          </div>

          {/* Tab bar */}
          <div className="flex shrink-0 items-center border-b border-[var(--border-hairline)] px-2 pt-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative flex items-center px-2.5 pb-1.5 text-[11px] font-medium tracking-wide",
                  "transition-colors duration-100",
                  activeTab === tab.id
                    ? "text-[var(--text-primary)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
                )}
              >
                {tab.label}
                {tab.id === "errors" && <ErrorBadge count={errorCount} />}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="bottom-panel-tab-indicator"
                    className="absolute inset-x-0 bottom-0 h-[1.5px] rounded-full bg-[var(--text-primary)]"
                    transition={spring}
                  />
                )}
              </button>
            ))}

            <button
              onClick={() => setOpen(false)}
              aria-label="Close panel"
              className={cn(
                "ml-auto flex items-center justify-center rounded-[var(--radius-sm)] p-1 mb-1",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
            >
              <X size={13} weight="bold" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
              >
                {tabContent[activeTab] ?? (
                  <p className="p-3 text-xs text-[var(--text-muted)]">No content</p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
